<template>
  <div class="page">
    <section v-if="isLoading" class="card">{{ t('loadingEventDetails') }}</section>
    <section v-else-if="errorMessage" class="card error">{{ errorMessage }}</section>

    <div v-else class="cover">
      <div v-if="eventData?.bannerUrl" class="cover-image">
        <img :src="eventData.bannerUrl" alt="Etkinlik banner" />
      </div>
      <header class="page-header">
        <div class="title-row">
          <div v-if="eventData?.eventPhotoUrl" class="title-photo">
            <img :src="eventData.eventPhotoUrl" alt="Etkinlik ana görseli" />
          </div>
          <div>
            <h1>{{ eventData?.name || t('eventLoading') }}</h1>
            <h3 v-if="eventData && eventData?.description">{{ eventData?.description || '' }}</h3>
          </div>
        </div>
        <router-link v-if="showWinnerLink" class="secondary" :to="winnerLink">
          {{ t('viewWinner') }}
        </router-link>
      </header>
    </div>

    <section v-if="!isLoading && !errorMessage" class="card">

      <div class="meta-grid">
        <div>
          <span>{{ t('start') }}</span>
          <strong>{{ formatDate(eventData.startsAt) }}</strong>
        </div>
        <div>
          <span>{{ t('end') }}</span>
          <strong>{{ formatDate(eventData.endsAt) }}</strong>
        </div>
      </div>

      <div v-if="eventData.hashtags?.length" class="event-hashtags">
        <div class="hashtags">
          <span v-for="tag in eventData.hashtags" :key="tag" class="tag">{{ tag }}</span>
        </div>
      </div>

      <h2 class="section-title">{{ t('items') }}</h2>

      <div v-if="eventData?.isFinished">
        <section class="card">
          <h3 class="section-title">{{ t('leaderboard') }}</h3>
          <div v-if="leaderboardError" class="card error">{{ leaderboardError }}</div>
          <div v-else-if="!leaderboardData">{{ t('loadingEventDetails') }}</div>
          <div v-else>
            <div v-if="leaderboardData.items?.length">
              <div class="winner">
                <div>
                  <p class="label">{{ t('topItem') }}</p>
                  <h2>{{ (leaderboardData.items[0] && leaderboardData.items[0].itemTitle) || '—' }}</h2>
                  <p class="subtext">{{ t('totalScore') }}: {{ leaderboardData.items[0].totalScore }}</p>
                </div>
                <span class="badge">{{ t('winner') }}</span>
              </div>

              <h3 class="section-subtitle">{{ t('allScoredItems') }}</h3>
              <ul class="list">
                <li v-for="item in leaderboardData.items" :key="item.itemId">
                  <span>{{ item.itemTitle }}</span>
                  <strong>{{ item.totalScore }}</strong>
                </li>
              </ul>
            </div>
            <div v-else class="empty">{{ t('noScoredItems') }}</div>
          </div>
        </section>
      </div>

      <div v-else class="items">
        <article v-for="item in eventData.items" :key="item.id" class="item">
          <div v-if="item.coverImageUrl" class="item-image">
            <img :src="item.coverImageUrl" :alt="item.title" />
          </div>
          <div class="item-header">
            <div>
              <h3>{{ item.title }}</h3>
              <p v-if="item.description" class="subtext">{{ item.description }}</p>
            </div>
            <router-link
              v-if="item.shortCode"
              class="arrow-button"
              :to="{
                name: 'event-item-questions',
                params: { eventSlug: eventData.slug, itemShortCode: item.shortCode }
              }"
              aria-label="Soruları aç"
            >
              →
            </router-link>
            <span v-else class="muted">{{ t('shortCodeRequired') }}</span>
          </div>
          <!-- social links shown as icons only -->
          <div v-if="item.socialLinks?.length" class="item-socials">
            <div class="links">
              <a
                v-for="link in item.socialLinks"
                :key="link"
                class="social-link"
                :href="link"
                target="_blank"
                rel="noopener"
                :title="link"
              >
                <span class="social-icon" :class="getSocialType(link)">
                  <span v-if="getSocialType(link) === 'instagram'">
                    <img src="/icons/instagram.png" alt="Instagram" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'tiktok'">
                    <img src="/icons/tiktok.png" alt="TikTok" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'twitter'">
                    <img src="/icons/twitter.png" alt="Twitter" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'pinterest'">
                    <img src="/icons/pinterest.png" alt="Pinterest" width="14" height="14" />
                  </span>
                  <span v-else>
                    <span class="link-prefix">www</span>
                  </span>
                </span>
              </a>
            </div>
          </div>
          <div v-if="item.totalScore !== undefined && item.totalScore !== null" class="score">
            <span>{{ t('totalScore') }}</span>
            <strong>{{ item.totalScore }}</strong>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getEventBySlug, getEventLeaderboard } from '../services/publicApi'
import { useI18n } from '../i18n'

export default {
  name: 'EventPage',
  setup() {
    const route = useRoute()
    const eventData = ref(null)
    const isLoading = ref(true)
    const errorMessage = ref('')
    const itemShortCode = ref('')
    const router = useRouter()
    const { t, locale } = useI18n()
    const leaderboardData = ref(null)
    const leaderboardError = ref('')

    const loadEvent = async () => {
      isLoading.value = true
      errorMessage.value = ''
      leaderboardData.value = null
      leaderboardError.value = ''
      try {
        eventData.value = await getEventBySlug(route.params.eventSlug)

        // If event is finished, load leaderboard instead of items
        if (eventData.value?.isFinished) {
          try {
            leaderboardData.value = await getEventLeaderboard(route.params.eventSlug)
          } catch (err) {
            leaderboardError.value = err.status === 400 ? t('eventNotFinished') : t('loadResultsFailed')
          }
        }
      } catch (error) {
        errorMessage.value = error.status === 404
          ? t('eventNotFound')
          : t('eventLoadFailed')
      } finally {
        isLoading.value = false
      }
    }

    const showWinnerLink = computed(() => {
      if (!eventData.value) return false
      return !eventData.value.isActive && !!eventData.value.endsAt
    })

    const winnerLink = computed(() => ({
      name: 'event-winner',
      params: { eventSlug: route.params.eventSlug }
    }))

    const openItem = () => {
      if (!itemShortCode.value) return
      router.push({
        name: 'event-item-questions',
        params: {
          eventSlug: route.params.eventSlug,
          itemShortCode: itemShortCode.value
        }
      })
    }

    const getSocialType = (link) => {
      const lower = link.toLowerCase()
      if (lower.includes('instagram.com')) return 'instagram'
      if (lower.includes('tiktok.com')) return 'tiktok'
      if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter'
      if (lower.includes('pinterest.com')) return 'pinterest'
      return 'www'
    }

    const formatDate = (value) => {
      if (!value) return '—'
      const formatter = new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Istanbul'
      })
      return formatter.format(new Date(value))
    }

    onMounted(loadEvent)

    const topLeaderboardItem = () => (leaderboardData.value?.items?.[0] || null)

    return {
      eventData,
      isLoading,
      errorMessage,
      itemShortCode,
      t,
      showWinnerLink,
      winnerLink,
      openItem,
      getSocialType,
      formatDate,
      leaderboardData,
      leaderboardError,
      topLeaderboardItem
    }
  }
}
</script>

<style scoped>
.page {
  display: grid;
  gap: 20px;
}

.section-title {
  font-size: 22px;
  margin: 30px 0 12px;
}

.cover {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: #e2e8f0;
  min-height: 220px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}

.cover-image {
  position: absolute;
  inset: 0;
}

.cover-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.25), rgba(15, 23, 42, 0.65));
}

.cover-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.page-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  color: #f8fafc;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-photo {
  width: 84px;
  height: 84px;
  border-radius: 20px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.25);
  border: 3px solid rgba(248, 250, 252, 0.85);
}

.title-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.eyebrow {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 8px;
}

.subtext {
  color: #64748b;
  margin: 4px 0 0;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.error {
  color: #b91c1c;
  border: 1px solid #fecaca;
  background: #fff1f2;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.event-hashtags {
  margin-bottom: 20px;
}

.event-hashtags span {
  display: block;
  color: #94a3b8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}


.page-header h1,
.page-header h3 {
  margin: 0;
}

.page-header h3 {
  opacity: 0.9;
  font-weight: 500;
}

.meta-grid span {
  display: block;
  color: #94a3b8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.meta-grid strong {
  font-size: 16px;
}

.items {
  display: grid;
  gap: 16px;
}

.item-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  background: #e2e8f0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.item-socials {
  display: flex;
  align-items: center;
  gap: 8px;
}

.arrow-button {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #2563eb;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.arrow-button:hover {
  background: #1d4ed8;
}

.social-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.social-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1e293b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #ffffff;
}

.social-icon.instagram,
.social-icon.tiktok,
.social-icon.twitter,
.social-icon.pinterest {
  background: #ffffff;
}

.link-prefix {
  font-weight: 700;
}

.lookup {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 20px;
}

.lookup label {
  display: grid;
  gap: 8px;
  font-weight: 600;
}

.lookup input {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
}

.item {
  display: grid;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  width: fit-content;
}

.pill.active {
  background: #dcfce7;
  color: #166534;
}

.pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.score span {
  color: #94a3b8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.score strong {
  display: block;
  font-size: 20px;
  margin-top: 4px;
}

.primary,
.secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
}

.primary {
  background: #2563eb;
  color: #ffffff;
}

.secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.primary:hover {
  background: #1d4ed8;
}

.secondary:hover {
  background: #cbd5f5;
}

.muted {
  color: #94a3b8;
  font-size: 14px;
}

/* Leaderboard styles */
.list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.winner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  border-radius: 14px;
  background: #fef9c3;
  margin-bottom: 20px;
}

.label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  color: #a16207;
  margin: 0 0 6px;
}

.badge {
  background: #facc15;
  color: #854d0e;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}
</style>
